import { useState } from "react";
import { Button, Chip, Typography } from "@mui/material";
import { makeStyles } from "@/compat/muiStyles";
import { Add } from "@mui/icons-material";
import CardBase from "../../../components/cards/CardBase";
import PeoplePickerDialog from "./PeoplePickerDialog";

const useStyles = makeStyles((theme) => ({
    chips: { display: "flex", flexWrap: "wrap", gap: theme.spacing(1) },
    empty: { color: theme.palette.text.secondary },
}));

type Person = { id?: any; name: string };
type CustomPerson = { id: any; name: string; siteExternalId?: string };

interface PeopleAtRiskCardProps {
    people: Person[];
    selectedPeopleIds: any[];
    customPeople: CustomPerson[];
    siteExternalId?: string;
    onSave: (result: { peopleIds: any[]; customPeople: CustomPerson[] }) => void;
}

/**
 * Shared "People at risk" section used by both the template builder and the RA
 * completion screen: shows selected people as removable chips and opens the
 * PeoplePickerDialog to add/edit. State-agnostic — the parent decides how onSave
 * applies (local state vs the completion reducer).
 */
export default function PeopleAtRiskCard({
    people,
    selectedPeopleIds,
    customPeople,
    siteExternalId = "",
    onSave,
}: PeopleAtRiskCardProps) {
    const classes = useStyles();
    const [open, setOpen] = useState(false);

    const selectedLibrary = (people ?? []).filter((p) => selectedPeopleIds?.includes(p.id));
    const total = selectedLibrary.length + (customPeople?.length ?? 0);

    const removeLibrary = (id: any) =>
        onSave({ peopleIds: selectedPeopleIds.filter((x) => x !== id), customPeople });
    const removeCustom = (id: any) =>
        onSave({ peopleIds: selectedPeopleIds, customPeople: customPeople.filter((c) => c.id !== id) });

    return (
        <CardBase title="People at risk" description="Who is typically at risk from these hazards.">
            {total === 0 ? (
                <Typography className={classes.empty}>No people added yet.</Typography>
            ) : (
                <div className={classes.chips}>
                    {selectedLibrary.map((p) => (
                        <Chip key={`p-${p.id}`} label={p.name} onDelete={() => removeLibrary(p.id)} />
                    ))}
                    {(customPeople ?? []).map((c) => (
                        <Chip
                            key={`c-${c.id}`}
                            label={c.name}
                            color="secondary"
                            variant="outlined"
                            onDelete={() => removeCustom(c.id)}
                        />
                    ))}
                </div>
            )}
            <Button
                fullWidth
                variant="outlined"
                color="secondary"
                startIcon={<Add />}
                onClick={() => setOpen(true)}
                sx={{ borderStyle: "dashed", py: 1.5, mt: 2 }}
            >
                Add people
            </Button>
            <PeoplePickerDialog
                open={open}
                onClose={() => setOpen(false)}
                people={people ?? []}
                selectedPeopleIds={selectedPeopleIds}
                customPeople={customPeople}
                siteExternalId={siteExternalId}
                onSave={onSave}
            />
        </CardBase>
    );
}
